import styled, { css } from 'styled-components';

export const CardContainer = styled.div`
  display: none;

  @container main-content (max-width: ${({ theme }) => theme.screenMD}px) {
    display: block;
  }
`;

export const CardGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.margin}px;
  grid-template-columns: 1fr;

  @container main-content (min-width: ${({ theme }) => theme.screenSM}px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const Card = styled.div`
  ${({ theme }) => css`
    padding: ${theme.padding}px;
    background: ${theme.colorBgContainer};
    border: ${`${theme.lineWidth}px solid ${theme.colorBorder}`};
    border-radius: ${theme.borderRadius}px;
    transition: all ${theme.motionDurationMid} ${theme.motionEaseInOut};
    position: relative;

    &:hover {
      border-color: ${theme.colorPrimary};
      box-shadow: ${theme.boxShadowSecondary};
    }

    @container main-content (min-width: ${({ theme }) => theme.screenSM}px) {
      /* Make the last card in an odd-numbered series span both columns */
      &:nth-child(odd):last-child {
        grid-column: 1 / -1;
      }
    }
  `}
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.marginSM}px;
`;

export const CardTitle = styled.div`
  ${({ theme }) => css`
    font-weight: ${theme.fontWeightStrong};
    font-size: ${theme.fontSizeLG}px;
    color: ${theme.colorTextHeading};
    line-height: ${theme.lineHeightLG};
  `}
`;

export const CardActions = styled.div`
  flex-shrink: 0;
  margin-left: ${({ theme }) => theme.marginXS}px;
`;

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginXS}px;
`;

export const CardField = styled.div<{
  $priority?: 'high' | 'medium' | 'low';
}>`
  ${({ $priority, theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: ${theme.marginXS}px;

    ${() => {
      switch ($priority) {
        case 'high':
          return `
          order: 1;
          padding-bottom: ${theme.marginXXS}px;
          border-bottom: ${theme.lineWidth}px solid ${theme.colorBorderSecondary};
        `;
        case 'medium':
          return `order: 2;`;
        case 'low':
          return `
          order: 3;
          opacity: 0.8;
        `;
        default:
          return `order: 2;`;
      }
    }}
  `}
`;

export const CardLabel = styled.span`
  color: ${({ theme }) => theme.colorTextLabel};
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  font-weight: ${({ theme }) => theme.fontWeightStrong};
  min-width: 80px;
  flex-shrink: 0;
`;

export const CardValue = styled.div`
  color: ${({ theme }) => theme.colorText};
  font-size: ${({ theme }) => theme.fontSize}px;
  text-align: right;
  flex: 1;
  word-break: break-word;
`;

export const EmptyStateContainer = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.paddingXL * 2}px
    ${({ theme }) => theme.padding}px;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

export const EmptyStateIcon = styled.div`
  font-size: ${({ theme }) => theme.fontSizeXL * 3}px;
  margin-bottom: ${({ theme }) => theme.margin}px;
  opacity: 0.3;
`;

export const LoadingCard = styled(Card)`
  opacity: 0.6;
  pointer-events: none;
`;

export const LoadingCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginXS}px;
`;

export const LoadingCardLine = styled.div<{ width?: string }>`
  height: ${({ theme }) => theme.fontSize}px;
  background: ${({ theme }) => theme.colorFillTertiary};
  border-radius: ${({ theme }) => theme.borderRadiusXS}px;
  width: ${({ width }) => width || '100%'};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
  }
`;
